import { useFormik } from "formik";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useAdminWeatherReportControllerDeleteWeatherReport,
  useAdminWeatherReportControllerFindOne,
  useAdminWeatherReportControllerUpdateWeatherReport,
} from "service";
import { AdminPutWeatherReportReqDto } from "service/api/estabrookSchemas";
import { useDialogStore } from "../../../state/dialog-store";
import DashboardContentLayout from "../components/dashboard-content-layout";
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Slider,
  TextField,
  Typography,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import { AdRoutes } from "../../../constants/ad-routes";
import { makeSxStyles } from "ui";
import { DateTime } from "luxon";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeyFn } from "service/api/estabrookContext";
import { formatDateTime } from "../../../utils/string-helpers";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "../../../shared/components/header-items/HeaderItems";
import Navbar from "../../../components/navbar/navbar";
import useUnsavedChangesBlocker from "../../../shared/hooks/useUnsavedChangesBlocker";
import { useSocketEvents } from "../../../web-sockets/use-socket-events";
import WeatherSeveritySelector, {
  WeatherSeverity,
} from "../snow-reports/components/weather-severity-selector";
import ElevationLevelSliders, {
  ElevationLevelValue,
  snowValueFieldSx,
} from "../snow-reports/components/elevation-level-sliders";

interface EditFormValues {
  severity: WeatherSeverity;
  measurementSystem: "imperial";
  snowAmount: number;
  levels: ElevationLevelValue[];
}

const SnowReportsDetailPage = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const { setDialog } = useDialogStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const styles = useStyles();
  const { isLoading, data } = useAdminWeatherReportControllerFindOne({
    pathParams: { reportId: reportId || "" },
  });

  const report = data?.data;
  const isElevation = (report?.elevationLevels?.length ?? 0) > 0;

  // Connect to WebSocket to listen for weather report events
  useSocketEvents({
    currentPage: "snow-reports",
  });
  const { mutate, isLoading: isSaving } =
    useAdminWeatherReportControllerUpdateWeatherReport({
      onSuccess: () => {
        toast.success("Report Modified Successfully");
        queryClient.invalidateQueries(
          queryKeyFn({
            path: "/api/v1/admin/weather-reports/{reportId}",
            operationId: "adminWeatherReportControllerFindOne",
            variables: { pathParams: { reportId: reportId || "" } },
          }),
        );
        queryClient.invalidateQueries(
          queryKeyFn({
            path: "/api/v1/admin/weather-reports",
            operationId: "adminWeatherReportControllerGetAll",
            variables: {},
          }),
        );
      },
    });

  const { mutate: removeReport, isLoading: isDeleting } =
    useAdminWeatherReportControllerDeleteWeatherReport({
      onSuccess: () => {
        toast.success("Report Deleted Successfully");
        queryClient.invalidateQueries(
          queryKeyFn({
            path: "/api/v1/admin/weather-reports",
            operationId: "adminWeatherReportControllerGetAll",
            variables: {},
          }),
        );
        navigate(-1);
      },
    });

  const buildBody = (
    formValues: EditFormValues,
  ): AdminPutWeatherReportReqDto =>
    isElevation
      ? {
          severity: formValues.severity,
          measurementSystem: formValues.measurementSystem,
          elevationLevels: formValues.levels.map((level) => ({
            id: level.id as string,
            min: level.min,
            max: level.max,
            snowAmount: level.snowAmount,
          })),
        }
      : {
          severity: formValues.severity,
          measurementSystem: formValues.measurementSystem,
          snowAmount: formValues.snowAmount,
        };

  const onUpdateReport = () => {
    setDialog({
      title: "Save changes",
      description: "Are you sure you want to save changes?",
      confirmText: "Save",
      withCancelAction: true,
      cancelText: "Cancel",
      onConfirm: async (onClose) => {
        mutate({
          pathParams: { reportId: reportId || "" },
          body: buildBody(values),
        });
        onClose();
      },
    });
  };

  const { values, dirty, isValid, setFieldValue, resetForm } =
    useFormik<EditFormValues>({
      initialValues: {
        severity: report?.severity || "rain",
        snowAmount: report?.snowAmount?.imperial.value || 0,
        measurementSystem: "imperial",
        levels: [],
      },
      onSubmit: () => onUpdateReport(),
    });

  useUnsavedChangesBlocker(dirty);

  const onDeleteReport = () => {
    setDialog({
      title: "Delete Weather Report",
      description:
        "Before deleting this weather report, please note that doing so will result in the permanent loss of all related information. Are you sure you want to continue?",
      confirmText: "Delete",
      cancelText: "Cancel",
      withCancelAction: true,
      onConfirm: async (onClose) => {
        removeReport({ pathParams: { reportId: reportId || "" } });
        onClose();
      },
    });
  };

  useEffect(() => {
    if (!isLoading && report) {
      resetForm({
        values: {
          measurementSystem: "imperial",
          severity: report.severity || "rain",
          snowAmount: report.snowAmount?.imperial.value || 0,
          levels: (report.elevationLevels ?? []).map((level) => ({
            id: level.id,
            min: level.min,
            max: level.max,
            snowAmount: level.snowAmount.imperial.value,
          })),
        },
      });
    }
  }, [data, isLoading]);

  if (isLoading || !report) {
    return (
      <DashboardContentLayout>
        <Navbar showAccountMenu>
          <HeaderItems>
            <HeaderItemsLeft
              breadcrumbs={[
                {
                  label: "Snow Reports",
                  url: AdRoutes.snowReports,
                },
                {
                  label: "Edit Snow Report",
                },
              ]}
            />
          </HeaderItems>
        </Navbar>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{ mt: "80px" }}
        >
          <CircularProgress />
        </Box>
      </DashboardContentLayout>
    );
  }

  return (
    <DashboardContentLayout>
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft
            breadcrumbs={[
              {
                label: "Snow Reports",
                url: AdRoutes.snowReports,
              },
              {
                label: "Edit Snow Report",
              },
            ]}
          />
          <HeaderItemsRight>
            <Button
              disabled={isLoading || isSaving || !dirty || !isValid}
              variant="contained"
              onClick={onUpdateReport}
              data-testid="save-changes-button"
              startIcon={
                isSaving ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
            >
              {isSaving ? "Saving..." : "Save changes"}
            </Button>
          </HeaderItemsRight>
        </HeaderItems>
      </Navbar>
      <Box sx={styles.customerDetailContainer}>
        <Typography fontWeight={600} alignSelf="flex-start">
          Weather Severity
        </Typography>
        <WeatherSeveritySelector
          value={values.severity}
          onChange={(severity) => setFieldValue("severity", severity)}
        />

        {isElevation ? (
          // Each row is labelled with its own range, so the section needs no
          // heading. Levels themselves are only configurable when creating a
          // report — a report keeps the ones it was created with.
          <ElevationLevelSliders
            levels={values.levels}
            onChange={(index, snowAmount) =>
              setFieldValue(`levels.${index}.snowAmount`, snowAmount)
            }
          />
        ) : (
          <>
            <Typography fontWeight={600}>Current Snowfall</Typography>
            <Box
              display="flex"
              flexDirection="row"
              alignItems="center"
              gap="24px"
            >
              <TextField
                type="number"
                inputProps={{
                  style: { textAlign: "center" },
                  min: 0,
                  max: 40,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">″</InputAdornment>
                  ),
                }}
                sx={snowValueFieldSx}
                value={values.snowAmount ?? 0}
                onChange={(e) => {
                  const raw = e.target.value;
                  const n = raw === "" ? 0 : Number(raw);
                  setFieldValue("snowAmount", Math.max(0, Math.min(40, n)));
                }}
              />
              <Slider
                id="snowAmount"
                value={values.snowAmount}
                max={40}
                min={0}
                step={1}
                onChange={(_e, value) => setFieldValue("snowAmount", value)}
              />
            </Box>
            <Typography fontWeight={600}>Address</Typography>
            <Typography color="gray" data-testid="address">
              {report?.address}
            </Typography>
          </>
        )}

        <Typography fontWeight={600}>Reporter</Typography>
        <Typography color="gray" data-testid="reporter">
          {report?.reporter}
        </Typography>
        <Typography fontWeight={600}>Time Stamp</Typography>
        <Typography color="gray">
          {report?.createdAt &&
            formatDateTime(report.createdAt, DateTime.DATETIME_MED)}
        </Typography>
        <Typography fontWeight={600}>Modified</Typography>
        <Typography color="gray">
          {report?.updatedAt &&
            formatDateTime(report.updatedAt, DateTime.DATETIME_MED)}
        </Typography>
        <Typography fontWeight={600} alignSelf="start">
          Delete Weather Report
        </Typography>
        <Box display="grid" gap="20px" alignItems="flex-start">
          <Typography variant="caption">
            Before deleting this weather report, please note that doing so will
            result in the permanent loss of all related information.
          </Typography>
          <Button
            sx={styles.deleteButton}
            color="error"
            variant="outlined"
            onClick={onDeleteReport}
            disabled={isDeleting}
            startIcon={
              isDeleting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Delete />
              )
            }
          >
            {isDeleting ? "Deleting..." : "Delete Weather Report"}
          </Button>
        </Box>
      </Box>
    </DashboardContentLayout>
  );
};

const useStyles = makeSxStyles(() => ({
  customerDetailContainer: {
    mt: "40px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    rowGap: "40px",
    maxWidth: "50%",
    alignItems: "center",
  },
  deleteButton: {
    maxWidth: "230px",
  },
}));

export default SnowReportsDetailPage;
